import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PageSEO from "../../components/feature/PageSEO";
import ServiceCard from "./components/ServiceCard";
import FAQSection from "./components/FAQSection";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const siteUrl = (import.meta.env.VITE_SITE_URL ?? '').replace(/\/$/, '');

const servicesSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Web Design Services for Small Businesses | PixelCraft Agency",
    "description": "Professional web design, website redesign, and SEO services for small businesses. Affordable, effective digital solutions starting from £149.",
    "url": `${siteUrl || "https://example.com"}/services`,
    "inLanguage": "en-GB",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": siteUrl || "https://example.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": `${siteUrl || "https://example.com"}/services`
        }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Web Design for Small Businesses",
    "description": "Modern, mobile-friendly websites that look great and convert visitors into leads. SEO-ready structure, fast loading, and responsive design.",
    "provider": {
      "@type": "Organization",
      "name": "PixelCraft Agency",
      "url": siteUrl || "https://example.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "offers": {
      "@type": "Offer",
      "price": "149.00",
      "priceCurrency": "GBP",
      "description": "Complete small business website up to 5 pages"
    },
    "serviceType": "Web Design",
    "url": `${siteUrl || "https://example.com"}/services#web-design`
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Website Redesign for Small Businesses",
    "description": "Transform your outdated website into a modern, high-converting platform. Improved page speed, mobile experience, and better SEO structure.",
    "provider": {
      "@type": "Organization",
      "name": "PixelCraft Agency",
      "url": siteUrl || "https://example.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "serviceType": "Website Redesign",
    "url": `${siteUrl || "https://example.com"}/services#redesign`
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "SEO for Small Businesses",
    "description": "Local SEO strategies designed to help small businesses appear at the top of Google when customers are actively searching.",
    "provider": {
      "@type": "Organization",
      "name": "PixelCraft Agency",
      "url": siteUrl || "https://example.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "serviceType": "Search Engine Optimisation",
    "url": `${siteUrl || "https://example.com"}/services#seo`
  }
];

const services = [
  {
    id: "web-design",
    icon: "ri-layout-4-line",
    eyebrow: "Service 01",
    title: "Web Design for Small Businesses",
    desc: "We build modern, mobile-friendly websites that look great and actually convert visitors into leads. Every site we create is built around your business goals — not just how it looks, but how it performs for your digital presence.",
    benefits: [
      "Custom design tailored to your brand — no generic templates",
      "Conversion-focused layouts built to turn visitors into enquiries",
      "SEO-ready structure to help you increase online visibility",
      "Fast loading websites scored to pass Google's Core Web Vitals",
      "Responsive design — perfect on every device",
      "Content management system so you can update it yourself",
    ],
    included: [
      "Up to 10 pages",
      "Custom design",
      "Mobile optimisation",
      "On-page SEO setup",
      "Contact form",
      "Google Analytics",
      "SSL certificate",
      "1-month support",
    ],
    img: "https://public.readdy.ai/ai/img_res/fb6f2780b19db4c51e379919d515867e.jpg",
  },
  {
    id: "redesign",
    icon: "ri-refresh-line",
    eyebrow: "Service 02",
    title: "Website Redesign for Small Businesses",
    desc: "Got an outdated website that&apos;s embarrassing you? We&apos;ll transform it into a modern, high-converting platform. Website redesign is one of the fastest ways to improve your Google ranking, get more customers, and boost credibility.",
    benefits: [
      "Comprehensive audit of your existing site",
      "Modern, fresh design aligned with current standards",
      "Improved page speed and mobile experience",
      "Better SEO structure to improve Google ranking",
      "Retain your existing content and domain authority",
      "Seamless migration with zero downtime",
    ],
    included: [
      "Full site audit",
      "New custom design",
      "Content migration",
      "Speed optimisation",
      "SEO restructure",
      "Mobile redesign",
      "Redirect mapping",
      "2-month support",
    ],
    img: "https://readdy.ai/api/search-image?query=before%20and%20after%20website%20redesign%20comparison%2C%20modern%20clean%20website%20on%20dual%20monitors%2C%20creative%20agency%20workspace%2C%20warm%20studio%20lighting%2C%20professional%20desk%20setup%2C%20neutral%20warm%20background&width=700&height=500&seq=service-redesign-001&orientation=landscape",
    reversed: true,
  },
  {
    id: "seo",
    icon: "ri-search-2-line",
    eyebrow: "Service 03",
    title: "SEO for Small Businesses",
    desc: "Ranking on Google doesn&apos;t happen by accident. Our local SEO strategies are designed specifically for small business web developers and owners who want to appear at the top when their customers are actively searching.",
    benefits: [
      "Local SEO to rank for searches in your area",
      "Keyword research focused on buyer-intent searches",
      "On-page optimisation across all key pages",
      "Google Business Profile setup and optimisation",
      "Monthly ranking and traffic reports",
      "Content strategy to build long-term authority",
    ],
    included: [
      "SEO audit",
      "Keyword research",
      "On-page optimisation",
      "Google Business Profile",
      "Link building",
      "Monthly reporting",
      "Content strategy",
      "Competitor analysis",
    ],
    img: "https://readdy.ai/api/search-image?query=SEO%20analytics%20dashboard%20on%20laptop%20screen%20showing%20rising%20traffic%20graphs%2C%20clean%20home%20office%20desk%2C%20notebook%20and%20coffee%2C%20soft%20warm%20light%2C%20professional%20and%20productive%20atmosphere%2C%20neutral%20tones&width=700&height=500&seq=service-seo-001&orientation=landscape",
  },
];

function ServicesHero() {
  const { ref, visible } = useScrollAnimation(0.05);

  return (
    <section className="pt-32 pb-16 bg-white" ref={ref}>
      <div className="container-md">
        <nav className="text-sm text-zinc-400 mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-accent cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-zinc-700">Services</span>
        </nav>
        <div
          className={`max-w-2xl transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
            What we offer
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-zinc-900 leading-tight mb-5">
            Web Design Services
            <br />
            <span className="text-accent">for Small Businesses</span>
          </h1>
          <p className="text-zinc-600 text-lg leading-relaxed">
            From brand new websites to complete redesigns and <strong>SEO for small businesses</strong> — we deliver affordable, effective digital solutions that grow your business.
          </p>
        </div>

        {/* Quick nav */}
        <div className="flex flex-wrap gap-3 mt-10">
          {services.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="whitespace-nowrap inline-flex items-center gap-2 border border-zinc-200 hover:border-accent hover:text-accent text-zinc-600 text-sm font-medium px-4 py-2 rounded-full transition-all cursor-pointer"
            >
              <i className={`${s.icon} text-sm`} />
              {s.title.split(" ")[0]} {s.title.split(" ")[1]}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <main>
      <PageSEO
        title="Web Design Services for Small Businesses | PixelCraft Agency"
        description="Professional web design, website redesign, and SEO services for small businesses. Affordable, effective websites starting from £149. Get found on Google and get more customers."
        canonical="/services"
        schema={servicesSchemas}
      />
      <Navbar />
      <ServicesHero />
      {services.map((s) => (
        <ServiceCard key={s.id} {...s} />
      ))}
      <FAQSection />
      {/* Bottom CTA */}
      <section className="section-pad bg-stone-50">
        <div className="container-md text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-zinc-900 mb-5">
            Not sure which service you need?
          </h2>
          <p className="text-zinc-500 text-base max-w-xl mx-auto mb-6">
            Tell us about your business and we&apos;ll recommend the right solution. And remember — a complete website starts from just:
          </p>
          <div className="inline-flex items-center gap-4 bg-white border border-zinc-200 rounded-2xl px-8 py-4 mb-8">
            <span className="font-heading text-4xl font-bold text-zinc-900">£149<span className="text-accent">.</span>00</span>
            <div className="w-px h-10 bg-zinc-200" />
            <span className="text-zinc-600 text-sm text-left">Up to 5 pages<br />No hidden fees</span>
          </div>
          <div className="block">
            <Link
              to="/contact"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-accent hover:bg-accent-dark text-white font-semibold px-8 py-4 rounded-lg text-base transition-all cursor-pointer"
            >
              Get Started Today
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
